import { useResourcesApi } from '@/services/api/resourcesApi'
import { OptionsField } from '@/types/forms/'
import { DataProcessing } from '@/utils/dataProcessing'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'

const mapNationalityData = (n: any): OptionsField | null => {
    if (!n) return null
    if (typeof n === 'string') {
        const trimmed = n.trim()
        return trimmed ? { label: trimmed, value: trimmed } : null
    }

    const labelFields = [
        'label',
        'name',
        'nombre',
        'description',
        'descripcion',
        'gentilicio',
        'nationality'
    ]
    const valueFields = [
        'value',
        'code',
        'codigo',
        'id',
        'iso',
        'codeIso',
        'countryCode'
    ]

    const labelKey = labelFields.find((field) => n?.[field])
    const valueKey = valueFields.find((field) => n?.[field])

    if (!labelKey || !valueKey) {
        return null
    }

    const finalLabel = String(n[labelKey]).trim()
    const finalValue = String(n[valueKey]).trim()

    if (!finalLabel || !finalValue || 
        finalLabel.toLowerCase() === 'unknown' || 
        finalValue.toLowerCase() === 'unknown' ||
        finalLabel === '.') {
        return null
    }

    return {
        label: finalLabel,
        value: finalValue
    }
}

// eslint-disable-next-line max-lines-per-function
export function DemographicFunctions() {
    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const fetchCountries = async (
        mounted: boolean
    ): Promise<OptionsField[] | undefined> => {
        if (!mounted) {
            return
        }

        const response = await resourcesApi.getCountries()
        const processedCountries = DataProcessing.processCountriesData(response)

        return processedCountries
    }

    const fetchNationalitiesList = async (
        mounted: boolean
    ): Promise<OptionsField[] | undefined> => {
        if (!mounted) {
            return
        }

        const response = await resourcesApi.getNationalityList()
        const raw = response?.data || response || []

        const processedNationalitiesList: OptionsField[] = raw
            .map(mapNationalityData)
            .filter((n: OptionsField | null): n is OptionsField => n !== null)

        return processedNationalitiesList
    }

    const fetchDepartments = async (
        mounted: boolean,
        watchCountry: string,
        retryCount = 0,
        maxRetries = 3
    ): Promise<OptionsField[] | undefined> => {
        if (!mounted) {
            return
        }
        try {
            const response = await resourcesApi.getRegions(watchCountry)

            const departmentsData = response?.data || response || []

            const processedDepartments =
                DataProcessing.processDepartmentsData(departmentsData)

            if (processedDepartments.length === 0) {
                if (retryCount < maxRetries) {
                    setTimeout(
                        () =>
                            fetchDepartments(
                                mounted,
                                watchCountry,
                                retryCount + 1,
                                maxRetries
                            ),
                        1000
                    )

                    return
                }
            }

            return processedDepartments
        } catch {
            if (retryCount < maxRetries) {
                setTimeout(
                    () =>
                        fetchDepartments(
                            mounted,
                            watchCountry,
                            retryCount + 1,
                            maxRetries
                        ),
                    1000
                )

                return
            }

            return []
        }
    }

    const fetchCities = async (
        mounted: boolean,
        watchCountry: string,
        watchDepartment: string,
        retryCount = 0,
        maxRetries = 3
    ): Promise<OptionsField[] | undefined> => {
        if (!watchCountry || !watchDepartment || !mounted) {
            return
        }

        try {
            const response = await resourcesApi.getCities(
                watchCountry,
                watchDepartment
            )

            if (!Array.isArray(response) || response.length === 0) {
                if (retryCount < maxRetries) {
                    setTimeout(
                        () =>
                            fetchCities(
                                mounted,
                                watchCountry,
                                watchDepartment,
                                retryCount + 1,
                                maxRetries
                            ),
                        1000
                    )

                    return
                }
            }

            return DataProcessing.processCitiesData(response)
        } catch {
            if (retryCount < maxRetries) {
                setTimeout(
                    () =>
                        fetchCities(
                            mounted,
                            watchCountry,
                            watchDepartment,
                            retryCount + 1,
                            maxRetries
                        ),
                    1000
                )

                return
            }

            return []
        }
    }

    const fetchLocalidad = async (
        mounted: boolean,
        retryCount = 0,
        maxRetries = 3
    ): Promise<OptionsField[] | undefined> => {
        if (!mounted) {
            return
        }

        const response = await resourcesApi.getLocality()

        const localityData = response?.data || response || []
        const processedLocality =
            DataProcessing.processLocalityData(localityData)

        if (processedLocality.length === 0) {
            if (retryCount < maxRetries) {
                setTimeout(
                    () => fetchLocalidad(mounted, retryCount + 1, maxRetries),
                    1000
                )

                return
            }
        }

        return processedLocality
    }

    const fetchOptionsTimeResidence = async (
        mounted: boolean,
        retryCount = 0,
        maxRetries = 3
    ): Promise<OptionsField[] | undefined> => {
        if (!mounted) {
            return
        }

        const response = await resourcesApi.getResidenceTime()

        const timeResidenceData = response?.data || response || []
        const processedTimeResidence =
            DataProcessing.processTimeResidenceData(timeResidenceData)

        if (processedTimeResidence.length === 0) {
            if (retryCount < maxRetries) {
                setTimeout(
                    () =>
                        fetchOptionsTimeResidence(
                            mounted,
                            retryCount + 1,
                            maxRetries
                        ),
                    1000
                )

                return
            }
        }

        return processedTimeResidence
    }

    return {
        fetchCountries,
        fetchDepartments,
        fetchCities,
        fetchLocalidad,
        fetchNationalitiesList,
        fetchOptionsTimeResidence
    }
}
